import { LoadingSpinner } from "@cmp-src/loadings/LoadingSpinner";
import { cn } from "@gettactic/helpers";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-brand font-inter text-button font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-80 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
    {
        variants: {
            variant: {
                default:
                    "bg-primary text-primary-foreground hover:bg-primary-hover shadow-button disabled:bg-neutral-100 disabled:text-neutral-400 disabled:shadow-none",
                primary:
                    "bg-primary text-primary-foreground hover:bg-primary-hover shadow-button disabled:shadow-none",
                destructive:
                    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                destructiveOutline:
                    "bg-transparent border border-destructive text-destructive hover:bg-destructive/90 hover:text-destructive-foreground transition-colors",
                outline:
                    "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                trigger:
                    "flex border border-neutral-200 bg-background hover:bg-accent hover:text-accent-foreground max-w-full flex-row whitespace-nowrap rounded-lg",
                outlinePale:
                    "border-1 border-neutral-100 text-secondary-text bg-background hover:border-neutral-50 disabled:text-neutral-200",
                secondary: "bg-neutral-300 text-secondary-text hover:bg-neutral-400",
                outlineSecondary:
                    "bg-transparent border border-neutral-300 text-secondary-text hover:bg-neutral-100 transition-colors",
                secondaryDashboard:
                    "bg-neutral-100 text-secondary-text hover:bg-neutral-200",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                icon: "bg-transparent text-muted-foreground hover:text-primary",
                circleIcon:
                    "bg-transparent text-muted-foreground opacity-90 hover:opacity-100 h-9 w-9 rounded-full p-2 flex items-center jusify-center transition-opacity",
                info: "bg-blue-100 text-blue-800 hover:bg-blue-200 active:bg-blue-200",
                critical:
                    "bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900 active:bg-red-200 active:text-red-900",
                warning:
                    "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 active:bg-yellow-200",
                success:
                    "bg-green-100 text-green-800 hover:bg-green-200 active:bg-green-200"
            },
            severity: {
                info: "bg-blue-100 text-blue-800 hover:bg-blue-200 focus:shadow-outline-blue",
                critical:
                    "bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900 focus:shadow-outline-red"
            },
            size: {
                default: "h-10 px-4 py-2",
                xs: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                xl: "h-12 px-10 py-3 rounded-lg",
                icon: "h-10 w-10",
                trigger: "max-h-[42px] h-[42px]"
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
);

/** Icon can be a React node (JSX element) or a Lucide icon component */
type IconProp = React.ReactNode | LucideIcon;

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    isLoading?: boolean;
    loading?: boolean;
    iconLeft?: IconProp;
    iconRight?: IconProp;
    labelClassName?: string;
    /** Default className applied to icon components passed directly (not as JSX) */
    iconClassName?: string;
}

/** Render an icon prop - handles both JSX elements and icon components */
const renderIcon = (
    icon: IconProp | undefined,
    defaultClassName: string
): React.ReactNode => {
    if (!icon) return null;

    // If it's already a valid React element (JSX), return as-is
    if (React.isValidElement(icon)) {
        return icon;
    }

    // If it's a component (function or forwardRef), render with default className
    if (
        typeof icon === "function" ||
        (typeof icon === "object" && icon !== null)
    ) {
        const IconComponent = icon as LucideIcon;
        return <IconComponent className={defaultClassName} />;
    }

    return icon;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            isLoading,
            loading,
            iconLeft,
            iconRight,
            children,
            severity,
            labelClassName,
            iconClassName = "h-4 w-4",
            ...props
        },
        ref
    ) => {
        const Comp = asChild ? Slot : "button";
        const isLoadingState = isLoading || loading;

        const renderedIconLeft = renderIcon(iconLeft, iconClassName);
        const renderedIconRight = renderIcon(iconRight, iconClassName);

        const content = (
            <>
                {renderedIconLeft && (
                    <span className="inline-flex">{renderedIconLeft}</span>
                )}
                {children && (
                    <span
                        className={cn(
                            "flex h-full w-full items-center justify-center gap-2",
                            labelClassName
                        )}
                    >
            {children}
          </span>
                )}
                {renderedIconRight && (
                    <span className="inline-flex">{renderedIconRight}</span>
                )}
            </>
        );

        return (
            <Comp
                className={cn(buttonVariants({ variant, size, severity, className }))}
                ref={ref}
                {...props}
                disabled={isLoadingState || props.disabled}
                type={props.type || "button"}
            >
                {isLoadingState ? (
                    <span className="relative inline-flex items-center justify-center">
            <span className="invisible transition-opacity duration-200">
              {content}
            </span>
            <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-200">
              <LoadingSpinner variant="circular" />
            </span>
          </span>
                ) : (
                    content
                )}
            </Comp>
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
